import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom, forkJoin, of, Subscription } from 'rxjs';
import { CommonExplainModule } from '../../../../common-explain/common-explain.module';
import {
  DocumentProcessingStepEnum,
  isArchiveFile,
  MAX_REFERENCE_DOCUMENTS,
  ResponseProjectDocument,
  ResponseProjectUploadFile,
} from '../../../models/response-project-document';
import { UploaderChangeEvent } from '../../../models/uploader-change-event';
import {
  ResponseDocumentSubtype,
  SCOPE_INTERNAL_DOC,
  SUBTYPE_MEMOIRE_NAME,
  SUBTYPE_OTHER_DISPLAYED,
  SUBTYPE_OTHER_NAME,
} from '../../../models/response-document-subtype';
import { ResponseProjetAiMessageScenario } from '../../../models/response-project';
import { ApiResponseDocumentsService } from '../../../services/api/response-documents/api-response-documents.service';
import { ApiResponseProjectsService } from '../../../services/api/response-projects/api-response-projects.service';
import { DocumentImportContext } from '../../../services/document-import.service';
import { MAX_ARCHIVE_SIZE_BYTES, MAX_FILE_SIZE_BYTES } from '../../../services/document-import.constants';
import { ImportConflictSession } from '../../../services/import-conflict-session';
import { ProjectDocumentImportService } from '../../../services/project-document-import.service';
import { ResponseToastService } from '../../../services/response-toast.service';
import { TendersResponsesService, WsConnection } from '../../../services/tenders-responses.service';
import { CategorySelectorComponent } from '../category-selector/category-selector.component';
import { DocumentIdentityComponent } from '../document-identity/document-identity.component';
import { DocumentImportZoneComponent } from '../document-import-zone/document-import-zone.component';
import { DuplicateResolverComponent } from '../duplicate-resolver/duplicate-resolver.component';
import { UncategorizedNoticeComponent } from '../uncategorized-notice/uncategorized-notice.component';
import { IgnoredFilesNoticeComponent } from '../ignored-files-notice/ignored-files-notice.component';
import { DuplicateItem, DuplicateResolution } from '../dedup/dedup.model';
import { findUploadSubtype, uploadSubtypeDisplayName, uploadSubtypeSlug } from '../document-types';
import {
  AttachedLibraryDoc,
  PickerConfirmResult,
  PickerTab,
  ReferenceDocumentPickerComponent,
} from '../reference-document-picker/reference-document-picker.component';
import { RejectedFile } from '../../../models/rejected-file';
import { AutoSelectionCardComponent } from '../auto-selection-card/auto-selection-card.component';
import { ImportTipComponent } from '../import-tip/import-tip.component';
import { LibraryHealthBannerComponent } from '../library-health-banner/library-health-banner.component';
import { LibraryOnboardingCardComponent } from '../library-onboarding-card/library-onboarding-card.component';
import { RefAccessBarComponent } from '../ref-access-bar/ref-access-bar.component';

const PUSH_PROJECTS_PREVIEW = 2;

interface PushConfirmState {
  uid: string;
  libraryId: number;
  projects: { id: number; name: string; is_active?: boolean }[];
  expanded: boolean;
}

@Component({
  selector: 'app-reference-documents-section',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    CommonExplainModule,
    DocumentIdentityComponent,
    DocumentImportZoneComponent,
    CategorySelectorComponent,
    ReferenceDocumentPickerComponent,
    DuplicateResolverComponent,
    UncategorizedNoticeComponent,
    IgnoredFilesNoticeComponent,
    AutoSelectionCardComponent,
    ImportTipComponent,
    LibraryHealthBannerComponent,
    LibraryOnboardingCardComponent,
    RefAccessBarComponent,
  ],
  templateUrl: './reference-documents-section.component.html',
  styleUrls: ['./reference-documents-section.component.scss'],
})
export class ReferenceDocumentsSectionComponent implements OnInit, OnChanges, OnDestroy {
  readonly DocumentProcessingStepEnum = DocumentProcessingStepEnum;
  readonly scope = SCOPE_INTERNAL_DOC;
  readonly maxDocuments = MAX_REFERENCE_DOCUMENTS;

  isArchiveFile(file: ResponseProjectUploadFile): boolean {
    return isArchiveFile(file.name);
  }

  @Input() title = '';
  @Input() subtypeOptions: ResponseDocumentSubtype[] = [];
  @Input() projectId: string | null = null;
  @Input() defaultDocumentSubtypeId?: number;
  @Input() configMode = false;
  @Input() contentDisabled = false;
  @Input() set collapsed(value: boolean) {
    this.panelExpanded = !value;
  }

  @Output() uploadStateChange = new EventEmitter<UploaderChangeEvent>();
  @Output() continueClicked = new EventEmitter<void>();
  @Output() projectNotFound = new EventEmitter<void>();
  @Output() documentNotFound = new EventEmitter<void>();

  panelExpanded = true;
  pickerOpen = false;
  pickerInitialTab: PickerTab = 'library';

  libraryCheckingUid: string | null = null;
  pushConfirm: PushConfirmState | null = null;

  duplicates: DuplicateItem[] = [];
  ignoredFiles: RejectedFile[] = [];

  /** null until the library size is known — the banner area stays empty rather than flickering. */
  libraryTotal: number | null = null;
  /** Mémoire technique documents in the library; null while the subtype id is unknown. */
  libraryMemoireTotal: number | null = null;
  /** Set by "Plus tard" / "Ma bibliothèque actuelle suffit" on the onboarding card. */
  laterClicked = false;
  advancedOpen = false;
  libraryCardOpen = false;
  importCardOpen = false;
  importTipDismissed = false;

  /**
   * Subtype id the counts were fetched with: undefined before the first fetch, null when the mémoire
   * technique subtype was still unknown then — that case is worth one retry once subtypeOptions lands.
   */
  private countedMemoireSubtypeId?: number | null;
  private tempFileCounter = 0;
  private duplicateCounter = 0;
  private pendingProcess = false;
  private wsSubscriptions: Subscription[] = [];
  private activeWsConn: WsConnection | null = null;
  private readonly conflicts = new ImportConflictSession();
  private wsProcessingComplete = false;

  constructor(
    private apiDocuments: ApiResponseDocumentsService,
    private apiProjects: ApiResponseProjectsService,
    private tendersResponses: TendersResponsesService,
    private projectImport: ProjectDocumentImportService,
    private toast: ResponseToastService,
    private translate: TranslateService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.refreshLibraryTotal();
  }

  private get importContext(): DocumentImportContext {
    return {
      projectId: this.projectId,
      scope: this.scope,
      defaultSubtypeId: this.defaultDocumentSubtypeId,
    };
  }

  private get scopeState() {
    return this.tendersResponses.uploadStates()[this.tendersResponses.fileKey(this.projectId ?? 'new', this.scope)];
  }

  private get effectiveId(): string {
    return this.projectId ?? 'new';
  }

  get fileRows(): ResponseProjectUploadFile[] {
    return this.scopeState?.files.filter(f => !f.hidden) ?? [];
  }

  get sortedFileRows(): ResponseProjectUploadFile[] {
    const rows = this.fileRows;
    const key = rows.map(f => `${f.uid}:${f.processingStep === DocumentProcessingStepEnum.UNCATEGORIZED ? 'u' : 'c'}:${f.name}`).join('|');
    if (key !== this.sortedFileRowsKey) {
      this.sortedFileRowsKey = key;
      this.sortedFileRowsCache = [...rows].sort((a, b) => {
        const aUncat = a.processingStep === DocumentProcessingStepEnum.UNCATEGORIZED;
        const bUncat = b.processingStep === DocumentProcessingStepEnum.UNCATEGORIZED;
        if (aUncat !== bUncat) return aUncat ? -1 : 1;
        return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
      });
      return this.sortedFileRowsCache;
    }
    const byUid = new Map(rows.map(f => [f.uid, f]));
    return this.sortedFileRowsCache.map(cached => byUid.get(cached.uid) ?? cached);
  }

  get uploadBatchCompleted(): number {
    return this.scopeState?.uploadCompleted ?? 0;
  }

  get uploadBatchTotal(): number {
    return this.scopeState?.uploadTotal ?? 0;
  }

  get categorizationCompleted(): number {
    return this.scopeState?.categorizationCompleted ?? 0;
  }

  get categorizationTotal(): number {
    return this.scopeState?.categorizationTotal ?? 0;
  }

  get processingStep(): DocumentProcessingStepEnum.UPLOADING | DocumentProcessingStepEnum.CATEGORIZING | null {
    if (this.fileRows.some(f => f.processingStep === DocumentProcessingStepEnum.CATEGORIZING)) {
      return DocumentProcessingStepEnum.CATEGORIZING;
    }
    if (
      this.fileRows.some(
        f =>
          f.processingStep === DocumentProcessingStepEnum.UPLOADING ||
          f.processingStep === DocumentProcessingStepEnum.UPLOADED ||
          f.processingStep === DocumentProcessingStepEnum.EXTRACTING
      )
    ) {
      return DocumentProcessingStepEnum.UPLOADING;
    }
    return null;
  }

  get isImportingFilesPhase(): boolean {
    return this.fileRows.some(f => f.processingStep === DocumentProcessingStepEnum.IMPORTING);
  }

  get continueSubmitted(): boolean {
    return this.fileRows.length > 0 && !this.isImportingFilesPhase;
  }

  get uncategorizedCount(): number {
    return this.fileRows.filter(f => f.processingStep === DocumentProcessingStepEnum.UNCATEGORIZED).length;
  }

  get addedCount(): number {
    return this.fileRows.length;
  }

  get batchProgressPercent(): number {
    if (!this.uploadBatchTotal) return 0;
    return Math.min(100, (this.uploadBatchCompleted / this.uploadBatchTotal) * 100);
  }

  get hasDocuments(): boolean {
    return this.fileRows.length > 0;
  }

  get libraryCountLoaded(): boolean {
    return this.libraryTotal !== null;
  }

  get libraryEmpty(): boolean {
    return this.libraryTotal === 0;
  }

  /**
   * A library without a mémoire technique cannot carry a response, however many other documents it
   * holds. An unknown mémoire count never reads as low, so a failed lookup stays silent.
   */
  get libraryLow(): boolean {
    return !!this.libraryTotal && this.libraryMemoireTotal === 0;
  }

  get libraryNeedsAttention(): boolean {
    return this.libraryEmpty || this.libraryLow;
  }

  /** Onboarding takes over the whole body: nothing attached yet and the library cannot carry the project. */
  get showOnboarding(): boolean {
    return this.libraryNeedsAttention && !this.hasDocuments && !this.laterClicked;
  }

  get actionRequired(): boolean {
    return this.showOnboarding;
  }

  get showAutoSelection(): boolean {
    return this.libraryCountLoaded && !this.libraryEmpty && !(this.libraryLow && !this.hasDocuments);
  }

  /**
   * Advanced settings stay visible once the user opted out of filling the library first, and while
   * an import leaves conflicts or ignored files behind — those still need to be resolved.
   */
  get advancedAlwaysOpen(): boolean {
    return (
      this.hasDocuments || this.duplicates.length > 0 || this.ignoredFiles.length > 0 || (this.libraryNeedsAttention && this.laterClicked)
    );
  }

  get libraryDocuments(): ResponseProjectUploadFile[] {
    return this.sortedFileRows.filter(f => f.isInLibrary);
  }

  get importedDocuments(): ResponseProjectUploadFile[] {
    return this.sortedFileRows.filter(f => !f.isInLibrary);
  }

  get isProcessing(): boolean {
    return this.processingStep !== null;
  }

  get importCardProgressPercent(): number {
    if (this.processingStep === DocumentProcessingStepEnum.CATEGORIZING) {
      return this.categorizationTotal ? Math.min(100, (this.categorizationCompleted / this.categorizationTotal) * 100) : 0;
    }
    return this.batchProgressPercent;
  }

  get importCardProgressCompleted(): number {
    return this.processingStep === DocumentProcessingStepEnum.CATEGORIZING ? this.categorizationCompleted : this.uploadBatchCompleted;
  }

  get importCardProgressTotal(): number {
    return this.processingStep === DocumentProcessingStepEnum.CATEGORIZING ? this.categorizationTotal : this.uploadBatchTotal;
  }

  /** The import card exists as soon as something is being imported or already was. */
  get showImportCard(): boolean {
    return this.importedDocuments.length > 0 || this.isProcessing || this.isImportingFilesPhase;
  }

  /**
   * The tip points at the row-level "Ajouter à la bibliothèque" action, so it appears as soon as a
   * single row offers it rather than waiting for the whole batch to finish.
   */
  get showImportTip(): boolean {
    if (this.importTipDismissed) return false;
    return this.importedDocuments.some(
      f => f.processingStep === DocumentProcessingStepEnum.CATEGORIZED || f.processingStep === DocumentProcessingStepEnum.UNCATEGORIZED
    );
  }

  goToLibrary(): void {
    void this.router.navigate(['/tenders/library']);
  }

  onLaterClicked(): void {
    if (this.contentDisabled) return;
    this.laterClicked = true;
  }

  toggleAdvanced(): void {
    this.advancedOpen = !this.advancedOpen;
  }

  toggleLibraryCard(): void {
    this.libraryCardOpen = !this.libraryCardOpen;
  }

  toggleImportCard(): void {
    this.importCardOpen = !this.importCardOpen;
  }

  dismissImportTip(): void {
    this.importTipDismissed = true;
  }

  private get memoireSubtypeId(): number | undefined {
    return this.subtypeOptions.find(o => o.name === SUBTYPE_MEMOIRE_NAME)?.id;
  }

  /** Both counts land together, otherwise the head area flips from "auto" to "low" mid-load. */
  private refreshLibraryTotal(): void {
    const memoireId = this.memoireSubtypeId;
    this.countedMemoireSubtypeId = memoireId ?? null;

    forkJoin({
      total: this.apiDocuments.countInLibrary(),
      memoire: memoireId == null ? of(null) : this.apiDocuments.countInLibrary({ response_document_subtype_id: memoireId }),
    }).subscribe({
      next: ({ total, memoire }) => {
        this.libraryTotal = total;
        this.libraryMemoireTotal = memoire;
      },
      // Leave the counts unknown so we never claim an empty library on a transient failure.
      error: () => undefined,
    });
  }

  attachedByLibraryId: Record<number, AttachedLibraryDoc> = {};

  private sortedFileRowsCache: ResponseProjectUploadFile[] = [];
  private sortedFileRowsKey = '';
  private dedupTimers: ReturnType<typeof setTimeout>[] = [];

  get pushProjectsPreview(): { id: number; name: string; is_active?: boolean }[] {
    if (!this.pushConfirm) return [];
    return this.pushConfirm.expanded ? this.pushConfirm.projects : this.pushConfirm.projects.slice(0, PUSH_PROJECTS_PREVIEW);
  }

  get pushProjectsHiddenCount(): number {
    if (!this.pushConfirm || this.pushConfirm.expanded) return 0;
    return Math.max(0, this.pushConfirm.projects.length - PUSH_PROJECTS_PREVIEW);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['subtypeOptions'] && this.countedMemoireSubtypeId === null && this.memoireSubtypeId != null) {
      this.refreshLibraryTotal();
    }

    if (changes['projectId'] && this.projectId) {
      const isIntendedTarget = this.pendingProcess || this.projectId === this.tendersResponses.newProjectId();
      if (!isIntendedTarget) return;

      const tempKey = this.tendersResponses.fileKey('new', this.scope);
      const pending = this.tendersResponses.uploadStates()[tempKey]?.files ?? [];
      const hadPendingFiles = pending.length > 0;
      if (hadPendingFiles) {
        this.tendersResponses.setFiles('new', this.scope, []);
        this.tendersResponses.setFiles(this.projectId, this.scope, pending);
      }
      if (this.pendingProcess || hadPendingFiles) {
        this.pendingProcess = false;
        void this.processFiles();
      }
    }
  }

  ngOnDestroy(): void {
    this.clearDedupTimers();
    if (this.projectId && this.tendersResponses.hasActiveProcessing(this.projectId, this.scope)) {
      this.activeWsConn = null;
      return;
    }
    this.unsubscribeFromWs();
    this.activeWsConn?.disconnect();
    this.activeWsConn = null;
  }

  /** Action-required pins the panel open: the user has nothing attached and an empty-ish library. */
  get expanded(): boolean {
    return this.actionRequired || this.panelExpanded;
  }

  get collapsible(): boolean {
    return !this.actionRequired;
  }

  togglePanelExpanded(value?: boolean): void {
    const next = value !== undefined ? value : !this.expanded;
    if (!next && !this.collapsible) return;
    this.panelExpanded = next;
  }

  onImportZoneFiles(files: File[]): void {
    if (this.contentDisabled || !files.length) return;
    this.addImportFiles(files);
    this.continueClicked.emit();
    if (this.projectId) {
      void this.processFiles();
    } else {
      this.pendingProcess = true;
    }
  }

  onImportZoneRejected(rejected: RejectedFile[]): void {
    if (!rejected.length) return;
    this.ignoredFiles = [...this.ignoredFiles, ...rejected];
  }

  openPicker(tab: PickerTab): void {
    if (this.contentDisabled) return;
    const map: Record<number, AttachedLibraryDoc> = {};
    for (const f of this.fileRows) {
      if (f.isInLibrary && f.documentId != null) {
        map[f.documentId] = { projectDocId: f.documentId, subtypeId: this.subtypeIdOf(f) };
      }
    }
    this.attachedByLibraryId = map;
    this.pickerInitialTab = tab;
    this.pickerOpen = true;
    this.panelExpanded = true;
  }

  closePicker(): void {
    this.pickerOpen = false;
  }

  subtypeNameOf(file: ResponseProjectUploadFile): string | null {
    return uploadSubtypeSlug(file, this.subtypeOptions);
  }

  getSubTypeDisplayName(file: ResponseProjectUploadFile): string {
    return uploadSubtypeDisplayName(file, this.subtypeOptions);
  }

  selectSubtype(file: ResponseProjectUploadFile, option: ResponseDocumentSubtype): void {
    this.tendersResponses.patchFile(this.effectiveId, this.scope, file.uid, {
      subtype: option.displayedName,
      subtypeId: option.id,
      processingStep: DocumentProcessingStepEnum.CATEGORIZED,
    });
    if (file.documentId) {
      this.apiDocuments.updateDocument({ id: file.documentId, response_document_subtype_id: option.id }).subscribe({
        error: (err: { status?: number }) => {
          if (err.status === 404) {
            this.tendersResponses.removeFile(this.effectiveId, this.scope, file.uid);
            this.documentNotFound.emit();
          }
        },
      });
    }
  }

  selectCustomSubtype(file: ResponseProjectUploadFile, customName: string): void {
    const autreOption = this.subtypeOptions.find(opt => opt.name === SUBTYPE_OTHER_NAME);
    this.tendersResponses.patchFile(this.effectiveId, this.scope, file.uid, {
      subtype: SUBTYPE_OTHER_DISPLAYED,
      subtypeId: autreOption?.id,
      customName,
      processingStep: DocumentProcessingStepEnum.CATEGORIZED,
    });
    if (file.documentId) {
      this.apiDocuments
        .updateDocument({
          id: file.documentId,
          custom_name: customName,
          ...(autreOption && { response_document_subtype_id: autreOption.id }),
        })
        .subscribe({
          error: (err: { status?: number }) => {
            if (err.status === 404) {
              this.tendersResponses.removeFile(this.effectiveId, this.scope, file.uid);
              this.documentNotFound.emit();
            }
          },
        });
    }
  }

  onRemove(file: ResponseProjectUploadFile): void {
    if (this.pushConfirm?.uid === file.uid) this.pushConfirm = null;

    this.tendersResponses.removeFile(this.effectiveId, this.scope, file.uid);
    this.uploadStateChange.emit({ files: [...this.fileRows], scenario: null });

    if (!file.documentId) return;

    if (file.isInLibrary) {
      if (!this.projectId) return;
      this.apiDocuments.unlinkFromProject(file.documentId, Number(this.projectId)).subscribe({
        error: (err: { status?: number }) => {
          if (err.status === 404) this.documentNotFound.emit();
        },
      });
    } else {
      this.apiDocuments.deleteDocument(file.documentId).subscribe({
        error: (err: { status?: number }) => {
          if (err.status === 404) this.documentNotFound.emit();
        },
      });
    }
  }

  async onLibraryAction(file: ResponseProjectUploadFile): Promise<void> {
    if (!file.documentId || file.isInLibrary || this.libraryCheckingUid) return;

    this.pushConfirm = null;
    this.libraryCheckingUid = file.uid;

    try {
      const res = await firstValueFrom(this.apiDocuments.findByNameInLibrary(file.name));
      const match = res.data.find(d => d.name === file.name && Number(d.id) !== file.documentId);

      if (!match) {
        await firstValueFrom(this.apiDocuments.updateDocument({ id: file.documentId, is_in_library: true }));
        this.tendersResponses.patchFile(this.effectiveId, this.scope, file.uid, { isInLibrary: true });
        this.toast.show(this.translate.instant('tenders.responses.documents.added-to-library-toast'));
        this.refreshLibraryTotal();
        return;
      }

      const projects = (match.projects ?? []).map(p => ({ id: p.id, name: p.name, is_active: p.is_active }));
      this.pushConfirm = {
        uid: file.uid,
        libraryId: Number(match.id),
        projects,
        expanded: false,
      };
    } catch {
      this.toast.show(this.translate.instant('tenders.responses.documents.library-action-error'));
    } finally {
      this.libraryCheckingUid = null;
    }
  }

  cancelPushConfirm(): void {
    this.pushConfirm = null;
  }

  togglePushProjectsExpanded(): void {
    if (!this.pushConfirm) return;
    this.pushConfirm = { ...this.pushConfirm, expanded: !this.pushConfirm.expanded };
  }

  async confirmPushReplace(file: ResponseProjectUploadFile): Promise<void> {
    if (!this.pushConfirm || !file.documentId) return;
    const libraryId = this.pushConfirm.libraryId;
    this.pushConfirm = null;
    this.libraryCheckingUid = file.uid;
    try {
      await this.replaceLibraryDocument(file, libraryId);
    } finally {
      this.libraryCheckingUid = null;
    }
  }

  /** Replace an existing library doc with the project file, then attach that library doc to the project. */
  private async replaceLibraryDocument(file: ResponseProjectUploadFile, libraryId: number): Promise<void> {
    if (!file.documentId) return;
    const projectDocId = file.documentId;

    try {
      const projectDoc = await firstValueFrom(this.apiDocuments.getDocument(projectDocId));
      const fileBlob = await this.fetchDocumentAsFile(projectDoc);
      const subtypeId = findUploadSubtype(file, this.subtypeOptions)?.id ?? this.defaultDocumentSubtypeId;

      const created = await firstValueFrom(
        this.apiDocuments.createDocument({
          id: libraryId,
          file_size: fileBlob.size,
          name: fileBlob.name,
          is_in_library: true,
          response_document_subtype_id: subtypeId,
        })
      );
      await firstValueFrom(this.apiDocuments.uploadToPresignedUrl(created.presigned_url, fileBlob, created.content_type));
      await firstValueFrom(this.apiDocuments.updateDocument({ id: libraryId, status: 'uploaded' }));

      const duplicateLibraryUid = this.fileRows.find(f => f.documentId === libraryId && f.uid !== file.uid)?.uid;

      if (projectDocId !== libraryId) {
        await firstValueFrom(this.apiDocuments.deleteDocument(projectDocId));
        if (this.projectId) {
          await firstValueFrom(this.apiDocuments.updateDocument({ id: libraryId, response_project_id: Number(this.projectId) }));
        }
        const libraryDoc = await firstValueFrom(this.apiDocuments.getDocument(libraryId));
        if (duplicateLibraryUid) {
          this.tendersResponses.removeFile(this.effectiveId, this.scope, duplicateLibraryUid);
        }
        this.replaceLocalRowWithLibraryDoc(file.uid, libraryDoc);
      } else {
        this.tendersResponses.patchFile(this.effectiveId, this.scope, file.uid, { isInLibrary: true });
        if (duplicateLibraryUid) {
          this.tendersResponses.removeFile(this.effectiveId, this.scope, duplicateLibraryUid);
        }
      }

      if (duplicateLibraryUid) {
        this.uploadStateChange.emit({ files: [...this.fileRows], scenario: null });
      }

      this.toast.show(this.translate.instant('tenders.responses.documents.added-to-library-toast'));
      this.refreshLibraryTotal();
    } catch {
      this.toast.show(this.translate.instant('tenders.responses.documents.library-action-error'));
    }
  }

  async onPickerConfirm(result: PickerConfirmResult): Promise<void> {
    this.pickerOpen = false;

    const detachedCount = result.detachDocumentIds.length;
    for (const docId of result.detachDocumentIds) {
      const row = this.fileRows.find(f => f.documentId === docId);
      if (row) {
        this.onRemove(row);
      } else {
        await this.detachByDocumentId(docId);
      }
    }

    let attachedCount = 0;
    for (const libraryId of result.attachIds) {
      if (await this.attachLibraryDocument(libraryId)) attachedCount++;
    }

    if (result.importFiles.length) {
      this.addImportFiles(result.importFiles);
      this.continueClicked.emit();
      if (attachedCount) this.showImportSuccessToast(attachedCount);
      if (this.projectId) {
        void this.processFiles();
      } else {
        this.pendingProcess = true;
      }
    } else if (result.attachIds.length || result.detachDocumentIds.length) {
      this.continueClicked.emit();
      this.uploadStateChange.emit({ files: [...this.fileRows], scenario: null });
      this.showPickerConfirmToast(attachedCount, detachedCount);
    }
  }

  private async detachByDocumentId(docId: number): Promise<void> {
    const row = this.fileRows.find(f => f.documentId === docId);
    const isInLibrary = row?.isInLibrary ?? true;
    try {
      if (isInLibrary) {
        if (!this.projectId) return;
        await firstValueFrom(this.apiDocuments.unlinkFromProject(docId, Number(this.projectId)));
      } else {
        await firstValueFrom(this.apiDocuments.deleteDocument(docId));
      }
    } catch {
      this.toast.show(this.translate.instant('tenders.responses.documents.library-action-error'));
    }
  }

  private async attachLibraryDocument(libraryId: number): Promise<boolean> {
    if (!this.projectId) return false;
    if (this.fileRows.length >= this.maxDocuments) return false;
    if (this.fileRows.some(f => f.documentId === libraryId)) return false;

    try {
      await firstValueFrom(this.apiDocuments.updateDocument({ id: libraryId, response_project_id: Number(this.projectId) }));
      const doc = await firstValueFrom(this.apiDocuments.getDocument(libraryId));
      this.appendLibraryDoc(doc);
      this.uploadStateChange.emit({ files: [...this.fileRows], scenario: null });
      return true;
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      if (status === 404) this.documentNotFound.emit();
      return false;
    }
  }

  private showPickerConfirmToast(attachedCount: number, detachedCount: number): void {
    if (attachedCount) {
      this.showImportSuccessToast(attachedCount);
      return;
    }
    if (detachedCount) this.showRemovedToast(detachedCount);
  }

  private showImportSuccessToast(count: number): void {
    this.showCountToast(count, 'import-success-toast');
  }

  private showRemovedToast(count: number): void {
    this.showCountToast(count, 'import-removed-toast');
  }

  private showCountToast(count: number, keyPrefix: 'import-success-toast' | 'import-removed-toast'): void {
    if (count < 1) return;
    const key = count === 1 ? `tenders.responses.documents.${keyPrefix}-one` : `tenders.responses.documents.${keyPrefix}-many`;
    this.toast.show(this.translate.instant(key, { count }));
  }

  private appendLibraryDoc(doc: ResponseProjectDocument): void {
    this.tendersResponses.appendFile(this.effectiveId, this.scope, this.libraryDocToFilePatch(doc));
  }

  private replaceLocalRowWithLibraryDoc(uid: string, doc: ResponseProjectDocument): void {
    this.tendersResponses.patchFile(this.effectiveId, this.scope, uid, this.libraryDocToFilePatch(doc));
  }

  private libraryDocToFilePatch(
    doc: ResponseProjectDocument
  ): Partial<ResponseProjectUploadFile> & Pick<ResponseProjectUploadFile, 'uid' | 'name'> {
    const subtype = this.subtypeOptions.find(s => s.id === doc.responseDocumentSubtypeId);
    const categorized = !!subtype && subtype.name !== 'undefined';
    return {
      uid: doc.uid,
      name: doc.name,
      size: doc.fileSize,
      documentId: Number(doc.id),
      subtype: categorized ? subtype!.displayedName : undefined,
      subtypeId: categorized ? subtype!.id : undefined,
      customName: doc.customName,
      processingStep: categorized ? DocumentProcessingStepEnum.CATEGORIZED : DocumentProcessingStepEnum.UNCATEGORIZED,
      isInLibrary: true,
    };
  }

  private async fetchDocumentAsFile(doc: ResponseProjectDocument): Promise<File> {
    const url = doc.downloadPresignedUrl;
    if (!url) throw new Error('missing download url');
    const response = await fetch(url);
    if (!response.ok) throw new Error('download failed');
    const blob = await response.blob();
    return new File([blob], doc.name, { type: blob.type || 'application/octet-stream' });
  }

  private addImportFiles(files: File[]): void {
    const batchNames = new Set<string>();
    let changed = false;

    for (const file of files) {
      if (batchNames.has(file.name)) continue;
      if (this.fileRows.length >= this.maxDocuments) break;

      const isArchive = isArchiveFile(file.name);
      const maxSize = isArchive ? MAX_ARCHIVE_SIZE_BYTES : MAX_FILE_SIZE_BYTES;
      if (file.size > maxSize) {
        this.ignoredFiles = [
          ...this.ignoredFiles,
          {
            name: file.name,
            reason: isArchive
              ? 'tenders.responses.documents.ignored-reason-too-large-archive'
              : 'tenders.responses.documents.ignored-reason-too-large',
          },
        ];
        continue;
      }

      const pendingSameName = this.fileRows.find(f => f.name === file.name && f.documentId == null);
      if (pendingSameName) this.tendersResponses.removeFile(this.effectiveId, this.scope, pendingSameName.uid);

      this.tendersResponses.appendFile(this.effectiveId, this.scope, {
        uid: `tmp-${++this.tempFileCounter}`,
        name: file.name,
        size: file.size,
        sourceFile: file,
        processingStep: DocumentProcessingStepEnum.IMPORTING,
        isInLibrary: false,
      });
      batchNames.add(file.name);
      changed = true;
    }

    if (!changed) return;
    this.importCardOpen = true;
    this.tendersResponses.setFiles(
      this.effectiveId,
      this.scope,
      [...this.fileRows].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
    );
    this.uploadStateChange.emit({ files: [...this.fileRows], scenario: null });
  }

  async processFiles(): Promise<void> {
    const toUpload = this.fileRows.filter(f => f.sourceFile instanceof File);
    if (!toUpload.length || !this.projectId) return;

    const priorDocIds = new Set(this.fileRows.filter(f => f.documentId != null && !toUpload.includes(f)).map(f => f.documentId!));

    this.unsubscribeFromWs();
    this.wsProcessingComplete = false;
    // Keep unresolved conflicts from earlier imports; only drop already-resolved rows.
    this.duplicates = this.duplicates.filter(d => !d.resolution);

    try {
      const result = await this.projectImport.run({
        projectId: this.projectId,
        scope: this.scope,
        ctx: this.importContext,
        files: toUpload,
        defaultSubtypeId: this.defaultDocumentSubtypeId,
        conflicts: this.conflicts,
        getFiles: () => this.fileRows,
        presentPieceByName: name => this.presentPieceByName(name),
        subtypeIdOf: file => this.subtypeIdOf(file),
        onConflict: (key, name, existingId, existingSubtypeId) => this.addConflict(key, name, existingId, existingSubtypeId),
        onConflictExpired: key => this.onConflictExpired(key),
        removeConflict: key => this.removeConflict(key),
        maybeCloseWs: () => this.maybeCloseWsConn(),
        ignoreFile: (name, reason) => {
          this.ignoredFiles = [...this.ignoredFiles, { name, reason }];
        },
        onUploadError: (file, stage, reason) => this.handleImportError(file, stage, reason),
        onCategorizing: () => {
          if (!this.configMode) this.togglePanelExpanded(false);
          this.uploadStateChange.emit({ files: [...this.fileRows], scenario: ResponseProjetAiMessageScenario.CATEGORIZING });
        },
        onCategorized: () => {
          this.uploadStateChange.emit({ files: [...this.fileRows], scenario: ResponseProjetAiMessageScenario.WORKING });
        },
        onProcessingComplete: () => {
          this.wsProcessingComplete = true;
          this.maybeCloseWsConn();
          const added = this.fileRows.filter(f => f.documentId != null && !isArchiveFile(f.name) && !priorDocIds.has(f.documentId)).length;
          this.showImportSuccessToast(added);
        },
      });
      if (result.wsConn) {
        this.activeWsConn = result.wsConn;
        this.wsSubscriptions.push(...result.subscriptions);
      }
    } finally {
      this.uploadStateChange.emit({ files: [...this.fileRows], scenario: ResponseProjetAiMessageScenario.READY });
    }
  }

  private onConflictExpired(conflictKey: string): void {
    const name = this.conflicts.get(conflictKey)?.name ?? this.duplicates.find(d => d.conflictKey === conflictKey)?.name;
    if (name) {
      this.ignoredFiles = [...this.ignoredFiles, { name, reason: 'tenders.responses.documents.ignored-reason-conflict-expired' }];
    }
    this.duplicates = this.duplicates.filter(d => d.conflictKey !== conflictKey || d.resolution);
    this.removeConflict(conflictKey);
  }

  private handleImportError(
    file: ResponseProjectUploadFile,
    stage: 'create' | 'upload',
    reason: 'not-found' | 'already-exists' | 'upload-error'
  ): void {
    if (this.projectId) {
      const isRegular = !isArchiveFile(file.name);
      if (stage === 'upload' || isRegular) this.tendersResponses.incrementUploadCompleted(this.projectId, this.scope);
      if (stage === 'create' && isRegular) this.tendersResponses.decrementCategorizationTotal(this.projectId, this.scope);
      this.tendersResponses.removeFile(this.projectId, this.scope, file.uid);
    }
    if (reason === 'not-found') {
      this.projectNotFound.emit();
    } else if (reason === 'already-exists') {
      const incoming = file.sourceFile instanceof File ? file.sourceFile : undefined;
      const localPiece = this.projectLocalPieceByName(file.name) ?? this.presentPieceByName(file.name);
      if (localPiece) {
        this.addDuplicate(file.name, localPiece.documentId, this.subtypeIdOf(localPiece), incoming);
      } else if (this.projectId) {
        this.apiDocuments.findByNameInProject(file.name, this.projectId).subscribe({
          next: res => {
            const scopeSubtypeIds = new Set(this.subtypeOptions.map(o => o.id));
            const named = res.data.filter(d => d.name === file.name);
            // Prefer a project-only copy so “replace” does not overwrite the library document.
            const projectLocal =
              named.find(d => !d.isInLibrary && scopeSubtypeIds.has(d.responseDocumentSubtypeId)) ??
              named.find(d => !d.isInLibrary) ??
              named.find(d => scopeSubtypeIds.has(d.responseDocumentSubtypeId)) ??
              named[0];
            if (projectLocal) {
              this.addDuplicate(file.name, Number(projectLocal.id), projectLocal.responseDocumentSubtypeId, incoming);
              return;
            }
            this.lookupLibraryDuplicate(file.name, incoming);
          },
          error: () => this.lookupLibraryDuplicate(file.name, incoming),
        });
      } else {
        this.lookupLibraryDuplicate(file.name, incoming);
      }
    } else {
      this.ignoredFiles = [...this.ignoredFiles, { name: file.name, reason: 'tenders.responses.documents.ignored-reason-upload-error' }];
    }
  }

  private lookupLibraryDuplicate(name: string, incoming: File | undefined): void {
    this.apiDocuments.findByNameInLibrary(name).subscribe({
      next: res => {
        const scopeSubtypeIds = new Set(this.subtypeOptions.map(o => o.id));
        const named = res.data.filter(d => d.name === name);
        const match = named.find(d => scopeSubtypeIds.has(d.responseDocumentSubtypeId)) ?? named[0];
        this.addDuplicate(name, match ? Number(match.id) : undefined, match?.responseDocumentSubtypeId, incoming);
      },
      error: () => this.addDuplicate(name, undefined, undefined, incoming),
    });
  }

  private addDuplicate(name: string, existingId: number | undefined, existingSubtypeId: number | undefined, file: File | undefined): void {
    if (this.duplicates.some(d => d.name === name && !d.resolution)) return;
    this.duplicates = [...this.duplicates, { uid: `dup-${++this.duplicateCounter}`, name, existingId, existingSubtypeId, file }];
  }

  private addConflict(conflictKey: string, name: string, existingId: number | undefined, existingSubtypeId: number | undefined): void {
    if (!this.conflicts.tryAdd(conflictKey, name, existingId, existingSubtypeId)) return;
    this.duplicates = [...this.duplicates, { uid: `dup-${++this.duplicateCounter}`, name, existingId, existingSubtypeId, conflictKey }];
    if (this.projectId) this.tendersResponses.decrementCategorizationTotal(this.projectId, this.scope);
  }

  private get hasPendingConflicts(): boolean {
    return this.conflicts.hasPending;
  }

  private removeConflict(conflictKey: string): void {
    this.conflicts.delete(conflictKey);
    this.maybeCloseWsConn();
  }

  private maybeCloseWsConn(): void {
    if (this.wsProcessingComplete && !this.hasPendingConflicts && this.activeWsConn) {
      this.activeWsConn.disconnect();
      this.activeWsConn = null;
    }
  }

  private presentPieceByName(name: string): ResponseProjectUploadFile | undefined {
    const candidates = this.fileRows.filter(
      f =>
        f.name === name &&
        f.documentId != null &&
        (f.processingStep === DocumentProcessingStepEnum.CATEGORIZED ||
          f.processingStep === DocumentProcessingStepEnum.UNCATEGORIZED ||
          f.processingStep === DocumentProcessingStepEnum.UPLOADED)
    );
    return candidates.find(f => !f.isInLibrary) ?? candidates[0];
  }

  private projectLocalPieceByName(name: string): ResponseProjectUploadFile | undefined {
    return this.fileRows.find(f => f.name === name && f.documentId != null && !f.isInLibrary);
  }

  /**
   * When both a project-only and a library-attached doc share a name, replace must
   * target the project copy so the library document is left untouched.
   */
  private resolveReplaceTarget(item: DuplicateItem): { id?: number; subtypeId?: number } {
    const projectLocal = this.projectLocalPieceByName(item.name);
    if (projectLocal?.documentId != null) {
      return { id: projectLocal.documentId, subtypeId: this.subtypeIdOf(projectLocal) ?? item.existingSubtypeId };
    }
    return { id: item.existingId, subtypeId: item.existingSubtypeId };
  }

  private subtypeIdOf(file: ResponseProjectUploadFile | undefined): number | undefined {
    return file ? (findUploadSubtype(file, this.subtypeOptions)?.id ?? file.subtypeId) : undefined;
  }

  private dismissDuplicate(item: DuplicateItem): void {
    this.dedupTimers.push(
      setTimeout(() => {
        item.phase = 'collapsing';
        this.duplicates = [...this.duplicates];
      }, 1500),
      setTimeout(() => {
        this.duplicates = this.duplicates.filter(d => d.uid !== item.uid);
      }, 1800)
    );
  }

  private clearDedupTimers(): void {
    for (const timer of this.dedupTimers) clearTimeout(timer);
    this.dedupTimers = [];
  }

  onDuplicateResolve(event: { item: DuplicateItem; resolution: DuplicateResolution }): void {
    const { item, resolution } = event;
    if (item.resolution) return;
    item.resolution = resolution;
    item.phase = 'leaving';
    this.duplicates = [...this.duplicates];

    if (item.conflictKey) {
      this.conflicts.markResolution(item.conflictKey, resolution);
      const target = resolution === 'replace' ? this.resolveReplaceTarget(item) : null;
      this.activeWsConn?.sendConflictResolution({
        action: resolution,
        key: item.conflictKey,
        document_id: resolution === 'replace' ? target?.id : undefined,
        document_subtype_id: resolution === 'replace' ? target?.subtypeId : undefined,
      });
    } else if (resolution === 'replace' && item.file) {
      this.replaceWithClientFile(item);
    } else if (resolution === 'keep' && item.existingId != null) {
      // Prefer the library / existing version: attach it to the project when missing.
      if (!this.fileRows.some(f => f.documentId === item.existingId)) {
        void this.attachLibraryDocument(item.existingId);
      }
    }

    this.dismissDuplicate(item);
  }

  private replaceWithClientFile(item: DuplicateItem): void {
    if (!item.file) return;
    const target = this.resolveReplaceTarget(item);
    if (target.id == null) {
      this.ignoredFiles = [...this.ignoredFiles, { name: item.name, reason: 'tenders.responses.documents.ignored-reason-replace-failed' }];
      return;
    }
    const existing = this.fileRows.find(f => f.documentId === target.id);
    const result = this.projectImport.queueReplace({
      projectId: this.projectId,
      scope: this.scope,
      ctx: { ...this.importContext, defaultSubtypeId: target.subtypeId ?? this.subtypeIdOf(existing) },
      uid: `tmp-${++this.tempFileCounter}`,
      file: item.file,
      replaceId: target.id,
      subtype: existing?.subtype ?? this.subtypeOptions.find(o => o.id === target.subtypeId)?.displayedName,
      customName: existing?.customName,
      existingUid: existing?.uid,
      onError: name => {
        this.ignoredFiles = [...this.ignoredFiles, { name, reason: 'tenders.responses.documents.ignored-reason-replace-failed' }];
      },
    });
    this.uploadStateChange.emit({ files: [...this.fileRows], scenario: null });
    if (result === 'pending') this.pendingProcess = true;
  }

  private unsubscribeFromWs(): void {
    this.wsSubscriptions.forEach(sub => sub.unsubscribe());
    this.wsSubscriptions = [];
  }

  openLibraryPicker(): void {
    this.openPicker('library');
    this.validateProjectInBackground();
  }

  openImportPicker(): void {
    this.openPicker('import');
    this.validateProjectInBackground();
  }

  /** Non-blocking existence check — open the picker immediately so the UI stays responsive. */
  private validateProjectInBackground(): void {
    if (!this.projectId) return;
    this.apiProjects.getById(this.projectId).subscribe({
      error: (err: { status?: number }) => {
        if (err.status === 404) {
          this.pickerOpen = false;
          this.projectNotFound.emit();
        }
      },
    });
  }
}
